import {
  cloneData, cloneDataOrNull, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../../models/dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';
import { ApplicationFeeProtocol } from '../../models/serializable/application-fee/application-fee-protocol.js';
import { VersionRange } from '../../models/core/version-range.js';

export class FeeSettingsComponent extends Component {
  static readonly TYPE_ID: string = 'a80d6b50091f43c89564d102412076a5';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly standardFee: ApplicationFeeProtocol,
    public readonly productFee: ApplicationFeeProtocol | null,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return FeeSettingsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, FeeSettingsComponent.SCHEMA_VERSION);
  }

  static create(args: {
    standardFee: ApplicationFeeProtocol,
    productFee: ApplicationFeeProtocol | null,
  }): FeeSettingsComponent {
    return new FeeSettingsComponent(args.standardFee, args.productFee);
  }

  static fromMap(context: SerializationContext, map: SerializedData): FeeSettingsComponent {
    return new FeeSettingsComponent(
        context.fromData(MapUtils.get<SerializedData>(map, 'standardFee')),
        context.fromDataOrNull(MapUtils.getOrNull<SerializedData>(map, 'productFee')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'standardFee': context.toData(this.standardFee),
      'productFee': context.toDataOrNull(this.productFee),
    };
  }

  clone(): this {
    return new FeeSettingsComponent(
        this.standardFee.clone(),
        this.productFee?.clone() ?? null,
    ) as this;
  }

  copyWith({
    standardFee = cloneData(this.standardFee),
    productFee = cloneDataOrNull(this.productFee),
  }: {
    standardFee?: ApplicationFeeProtocol,
    productFee?: ApplicationFeeProtocol | null,
  } = {}): this {
    return FeeSettingsComponent.create({
      standardFee,
      productFee,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.standardFee, this.standardFee) &&
        isSameData(other.productFee, this.productFee);
  }
}

export class FeeSettingsComponentBuilder extends DataModelBuilder<FeeSettingsComponent> {
  build(context: SerializationContext, map: SerializedData): FeeSettingsComponent {
    return FeeSettingsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(FeeSettingsComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(FeeSettingsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - Rename `feeProtocol` to `standardFee`.
     */
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'standardFee', MapUtils.getRecord(map, 'feeProtocol'));
      delete map['feeProtocol'];

      version = 2;
    }

    /**
     * Version 3:
     *  - Added `productFee` (nullable).
     */
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'productFee', null);

      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
